import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Stack, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
    Add as AddIcon, CheckCircle as CheckCircleIcon, Edit as EditIcon, HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
import { AccessStatus, ExecutionOverview, ProjectMember } from '../../../types/execution';
import executionService, { MemberPayload } from '../../../services/executionService';
import { errorMessage } from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

interface AccessViewProps {
    projectId: number;
    overview: ExecutionOverview;
    reload: () => void;
    notify: Notify;
}

const NEXT_STATUS: Record<AccessStatus, AccessStatus> = { PENDING: 'OK', OK: 'NA', NA: 'PENDING' };

const AccessIcon: React.FC<{ status: AccessStatus }> = ({ status }) => {
    if (status === 'OK') return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    if (status === 'NA') return <Typography variant="caption" color="text.secondary">n/a</Typography>;
    return <HourglassIcon fontSize="small" sx={{ color: 'warning.main' }} />;
};

const MEMBER_FIELDS: Array<{ field: keyof MemberPayload; label: string; md: number }> = [
    { field: 'squad', label: 'Squad', md: 3 },
    { field: 'squad_lead', label: 'Líder do squad', md: 3 },
    { field: 'client_user_code', label: 'Usuário no cliente', md: 4 },
    { field: 'hostname', label: 'Hostname', md: 4 },
    { field: 'access_owner', label: 'Responsável pela pendência', md: 4 },
];

interface MemberDialogProps {
    open: boolean;
    projectId: number;
    member: ProjectMember | null;
    onClose: () => void;
    onSaved: () => void;
    notify: Notify;
}

const MemberDialog: React.FC<MemberDialogProps> = ({ open, projectId, member, onClose, onSaved, notify }) => {
    const [form, setForm] = useState<MemberPayload>({});

    useEffect(() => {
        if (open) setForm(member ? { ...member } : { name: '' });
    }, [open, member]);


    const handleSave = async () => {
        const payload: MemberPayload = {};
        [...MEMBER_FIELDS.map((f) => f.field), 'access_notes' as const].forEach((field) => {
            (payload as any)[field] = form[field] === '' ? null : form[field];
        });
        try {
            if (member) await executionService.updateMember(member.id, payload);
            else await executionService.createMember(projectId, payload);
            onSaved();
            onClose();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar pessoa'), 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{member ? `Acessos — ${member.name}` : 'Pessoa'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {MEMBER_FIELDS.map(({ field, label, md }) => (
                        <Grid item xs={12} md={md} key={field}>
                            <TextField fullWidth label={label} value={(form[field] as string) || ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
                                required={field === 'name'} />
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} label="Status / pendências" value={form.access_notes || ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, access_notes: e.target.value }))}
                            placeholder="Ex.: VDI solicitada em 15/09, previsão 10 dias" />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

const AccessView: React.FC<AccessViewProps> = ({ projectId, overview, reload, notify }) => {
    const { items, members } = overview.access;
    const [newItem, setNewItem] = useState('');
    const [memberDialog, setMemberDialog] = useState<{ open: boolean; member: ProjectMember | null }>({ open: false, member: null });
    const [toDelete, setToDelete] = useState<ProjectMember | null>(null);

    const run = async (action: () => Promise<unknown>, fallback: string) => {
        try {
            await action();
            reload();
        } catch (error) {
            notify(errorMessage(error, fallback), 'error');
        }
    };

    const handleAddItem = () => {
        if (!newItem.trim()) return;
        run(() => executionService.createAccessItem(projectId, newItem.trim()), 'Erro ao criar item');
        setNewItem('');
    };


    // Agrupa por squad mantendo a ordem de cadastro
    const squads: string[] = [];
    members.forEach((m) => {
        const squad = m.squad || 'Sem squad';
        if (!squads.includes(squad)) squads.push(squad);
    });

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Itens do checklist de acessos</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                        {items.map((item) => (
                            <Chip key={item.id} label={item.name}
                                onDelete={() => run(() => executionService.deleteAccessItem(item.id), 'Erro ao excluir item')} />
                        ))}
                        <TextField size="small" placeholder="Novo item (ex.: VDI)" value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }} />
                        <IconButton size="small" onClick={handleAddItem} aria-label="Adicionar item" disabled={!newItem.trim()}><AddIcon /></IconButton>
                    </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px' }}>
                    O time do projeto são as pessoas alocadas nas posições (aba <b>Equipe</b> do projeto). Ao alocar
                    alguém, a pessoa aparece aqui automaticamente para o controle de acessos.
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={1}>
                Clique num item para alternar: pendente → ok → n/a. {overview.access.pending_count} pessoa(s) com acessos pendentes.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Squad</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>Usuário</TableCell>
                            <TableCell>Hostname</TableCell>
                            {items.map((item) => <TableCell key={item.id} align="center">{item.name}</TableCell>)}
                            <TableCell sx={{ minWidth: 200 }}>Status / pendências</TableCell>
                            <TableCell>Responsável</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {members.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={items.length + 7}>
                                    <Typography color="text.secondary">Nenhuma pessoa cadastrada.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {squads.map((squad) => members.filter((m) => (m.squad || 'Sem squad') === squad).map((member, index, list) => (
                            <TableRow key={member.id}>
                                {index === 0 && (
                                    <TableCell rowSpan={list.length} sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                        {squad}
                                        {member.squad_lead && <Typography variant="caption" display="block" color="text.secondary">{member.squad_lead}</Typography>}
                                    </TableCell>
                                )}
                                <TableCell>{member.name}</TableCell>
                                <TableCell>{member.client_user_code || '-'}</TableCell>
                                <TableCell>{member.hostname || '-'}</TableCell>
                                {items.map((item) => {
                                    const status = member.access[String(item.id)] || 'PENDING';
                                    return (
                                        <TableCell key={item.id} align="center">
                                            <IconButton size="small"
                                                onClick={() => run(() => executionService.setMemberAccess(member.id, item.id, NEXT_STATUS[status]), 'Erro ao atualizar acesso')}>
                                                <AccessIcon status={status} />
                                            </IconButton>
                                        </TableCell>
                                    );
                                })}
                                <TableCell sx={{ whiteSpace: 'pre-line' }}>
                                    {member.access_notes || (member.access_complete ? 'Completo' : '-')}
                                </TableCell>
                                <TableCell>{member.access_owner || '-'}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => setMemberDialog({ open: true, member })}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={1} display="flex" gap={2} alignItems="center">
                <AccessIcon status="OK" /><Typography variant="caption">ok</Typography>
                <AccessIcon status="PENDING" /><Typography variant="caption">pendente</Typography>
                <AccessIcon status="NA" /><Typography variant="caption">não se aplica</Typography>
            </Box>

            <MemberDialog
                open={memberDialog.open}
                projectId={projectId}
                member={memberDialog.member}
                onClose={() => setMemberDialog({ open: false, member: null })}
                onSaved={reload}
                notify={notify}
            />

            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>Remover pessoa</DialogTitle>
                <DialogContent><Typography>Remover {toDelete?.name} da lista de acessos?</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>Cancelar</Button>
                    <Button color="error" onClick={() => {
                        const member = toDelete;
                        setToDelete(null);
                        if (member) run(() => executionService.deleteMember(member.id), 'Erro ao remover pessoa');
                    }}>Remover</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AccessView;
